import { useControllableValue } from 'ahooks';
import { Input, Select, SelectProps } from 'antd';
import { DefaultOptionType } from 'antd/lib/select';
import classNames from 'classnames';
import React, { ReactElement, ReactNode, Ref, forwardRef, useImperativeHandle, CSSProperties, useEffect } from 'react';
import './index.scss';

export interface CharacterInputValue {
  mode?: string;
  values?: any;
}

export interface TdsFieldSelectorEnableExcludeValue{
  select_value?: any,
  switch_value?: boolean,
}

export interface CustomInputRenderProps {
  onChange: (value: any) => void;
  dom: ReactElement;
  value: any;
}

type PropsWithStyles<T> = T & {
  style?: CSSProperties;
  className?: string;
};

export interface CharacterInputProps {
  modeSelectProps?: SelectProps;
  valueSelectProps?: SelectProps;
  onChange?: (value: CharacterInputValue) => void;
  convert?: (value: CharacterInputValue) => CharacterInputValue;
  options?: DefaultOptionType[];
  value?: CharacterInputValue;
  defaultValue?: CharacterInputValue;
  customInputRender?: (props: CustomInputRenderProps) => ReactNode;
  onModeChange?: (mode: any) => void;
  onValuesChange?: (values: any) => void;
  defaultActiveFirstMode?: boolean;
}

/**
 * 字符串类型的查询表单输入组件
 * @param props
 * @returns
 */
export const CharacterInput = forwardRef((
  props: PropsWithStyles<CharacterInputProps>,
  ref: Ref<any>,
) => {
  const [value, setValue] = useControllableValue<CharacterInputValue>(props, {
    defaultValue: {
      mode: undefined,
      values: undefined,
    },
  });

  const getDefaultMode = () => {
    if (value.mode) return value.mode;
    return props.defaultActiveFirstMode
      ? (props.modeSelectProps?.options?.[0]?.value ?? undefined) as string
      : undefined;
  };

  const onModeChange = (mode: string) => {
    const nextValue = {
      mode,
      values: value?.values,
    };
    props.onModeChange?.(mode);
    setValue(props.convert ? props.convert(nextValue) : nextValue);
  };

  const onValueChange = (values: string | string[]) => {
    const nextValue = {
      values,
      mode: value.mode,
    };
    props.onValuesChange?.(values);
    setValue(props.convert ? props.convert(nextValue) : nextValue);
  };

  const renderInputComponent = () => {
    const defaultDom = <Select
      className={'values'}
      value={value.values}
      onChange={onValueChange}
      dropdownClassName={'reset-font-size'}
      {...props.valueSelectProps}
    />;

    if (props.customInputRender) {
      return props.customInputRender({
        onChange: onValueChange,
        dom: defaultDom,
        value: value.values,
      });
    }

    return defaultDom;
  };

  useImperativeHandle(ref, () => ({
    onModeChange,
    onValueChange,
    getValue: () => value,
  }));

  useEffect(() => {
    setValue({
      ...value,
      mode: getDefaultMode(),
    });
  }, []);

  return (
    <Input.Group className={classNames('search-form-group', props.className)} style={props.style}>
      {/* 查询模式 */}
      <Select
        className={'mode'}
        value={value.mode}
        onChange={onModeChange}
        dropdownClassName={'reset-font-size'}
        {...props.modeSelectProps}
      />
      {renderInputComponent()}
    </Input.Group>
  );
});
